import { ContentBox } from 'app/agriculture/components/ContentBox/ContentBox';
import { Actions } from 'app/agriculture/components/Actions/Actions';
import { DeviceIcon } from 'app/agriculture/icons/devices/DeviceIcon';
import { MapLocation } from 'app/agriculture/queries/types/MapLocation';
import { useDevice } from 'app/agriculture/queries/devices/useDevice';
import { useNavigate } from 'react-router-dom';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useEntityUpdateMutation } from 'app/agriculture/mutations/entity/update/useEntityUpdateMutation';
import { FormDiscardChangesBlocker } from 'app/agriculture/forms/components/FormDiscardChangesBlocker/FormDiscardChangesBlocker';
import { SpecificLocationField } from 'app/agriculture/forms/components/SpecificLocationField/SpecificLocationField';
import { Input } from 'app/agriculture/forms/components/Input/Input';
import './DeviceEditForm.scss';
import { DeviceTypesWithNoLocationOverride } from 'app/agriculture/queries/devices/types/DeviceType';
import { useDeviceLocation } from 'app/agriculture/queries/devices/useDeviceLocation';
import { ShowOnMapButton } from 'app/agriculture/components/ContentBox/components/ShowOnMapButton';
import { SensorCalibration } from 'app/agriculture/routes/Tanks/TankLink/components/SensorCalibration';
import { brandConfig } from 'app/agriculture/queries/snapshots/config/brandConfig';

interface DeviceEditFormProps {
    deviceId: string;
}

interface DeviceEditFields {
    name: string;
    locationOverride: MapLocation | null;
    tankMaxVolume: number | null;
    tankMaxHeight: number | null;
    tankCalibrationOffset: number | null;
}

export function DeviceEditForm({ deviceId }: DeviceEditFormProps) {
    const device = useDevice({ deviceId });
    const navigate = useNavigate();
    const location = useDeviceLocation({ deviceId });

    const formController = useForm<DeviceEditFields>({
        defaultValues: {
            name: device.name,
            locationOverride: device.locationOverride,
            tankMaxVolume: device.tankMaxVolume != null ? device.tankMaxVolume * 1000 : null,
            tankMaxHeight: device.tankMaxHeight,
            tankCalibrationOffset: device.tankCalibrationOffset,
        },
        mode: 'all',
        shouldFocusError: true,
    });

    const {
        register,
        reset,
        setValue,
        handleSubmit,
        formState: { errors, isDirty },
    } = formController;

    const { mutate, isPending } = useEntityUpdateMutation(brandConfig[device.brand].parentType);

    const onSubmit: SubmitHandler<DeviceEditFields> = ({
        tankCalibrationOffset,
        tankMaxHeight,
        tankMaxVolume,
        locationOverride,
        name,
    }) => {
        mutate(
            {
                id: device.id,
                tankCalibrationOffset,
                name,
                tankMaxHeight,
                tankMaxVolume: tankMaxVolume != null ? tankMaxVolume / 1000 : null,
                locationOverride,
            },
            {
                onSuccess: () => {
                    reset({}, { keepValues: true });
                    navigate('../');
                },
                onError: () => {
                    reset({}, { keepValues: true });
                },
            },
        );
    };

    return (
        <ContentBox
            title="Edit Device"
            Icon={(props) => <DeviceIcon {...props} deviceType={device.deviceType} />}
            variant="xl"
            showOnMapButton={<ShowOnMapButton position={location} />}
            className="DeviceEditForm"
        >
            <FormProvider {...formController}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <FormDiscardChangesBlocker />
                    <ContentBox.Section
                        scrollable
                        className="DeviceEditForm__fields"
                        disableDivider
                    >
                        <Input
                            label="Name"
                            {...register('name', { required: 'Please add a name' })}
                            errorMessage={errors.name?.message}
                        />
                        {!DeviceTypesWithNoLocationOverride.includes(device.deviceType) ? (
                            <SpecificLocationField
                                label="Fixed Location"
                                entityType="Device"
                                initialValue={device.locationOverride}
                                onChange={(value) => {
                                    setValue('locationOverride', value, { shouldDirty: true });
                                }}
                                defaultCoords={
                                    location == null
                                        ? null
                                        : { longitude: location[0], latitude: location[1] }
                                }
                            />
                        ) : null}
                        {device.deviceType === 'Level Monitor' ? (
                            <SensorCalibration small brand={device.brand} />
                        ) : null}
                        <Actions
                            sticky
                            primaryAction={{
                                text: 'Save device changes',
                                type: 'submit',
                                isLoading: isPending,
                                disabled: !isDirty,
                            }}
                            backAction={{
                                text: 'Back',
                                onClick: () => {
                                    navigate(-1);
                                },
                            }}
                        />
                    </ContentBox.Section>
                </form>
            </FormProvider>
        </ContentBox>
    );
}
